/**
 * A manual journal's lines: Account · Description · Contact · Branch · Cost
 * centre · Debit · Credit, and their totals. Editing, it shows each line's
 * problems where they stand and marks the cells its account requires; it
 * never adds a line to balance the entry. Read-only, it shows lines as posted.
 */
import type { JSX } from "react";
import { Trash2 } from "lucide-react";
import {
  Button,
  CodeEntityCombobox,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableRow,
  Icon,
  Input,
  nonEmpty,
  type CodeEntityRow,
} from "@linkhr/design-system";
import { useTranslation } from "@linkhr/i18n";
import { ZERO } from "@linkhr/accounting-domain";
import type { Account, Branch, Contact, CostCentre } from "../api/ledger";
import { GridHead } from "../components/GridHead";
import { MoneyInput } from "../components/MoneyInput";
import { useLedgerFormat } from "../ledger/format";
import { missingCells, type GridLine, type GridTotals } from "./lines";
import { problemText, type Problem } from "./refusal";

export interface GridRefs {
  /** The whole chart, for names and each account's rules. */
  accounts: readonly Account[];
  /** The accounts a line can take on the entry's date. */
  pickable: readonly Account[];
  contacts: readonly Contact[];
  branches: readonly Branch[];
  costCentres: readonly CostCentre[];
}

type Named = { id: string; code?: string; name: Readonly<Record<string, string>> };

const row = (item: Named, archived = false): CodeEntityRow => ({
  id: item.id,
  code: item.code ?? null,
  nameEn: item.name.en ?? item.name.ar ?? "",
  nameAr: item.name.ar ?? item.name.en ?? "",
  ...(archived ? { status: "ARCHIVED" } : {}),
});

/** The choices, plus the one already chosen when it is no longer among them (dimmed). */
export function choices<T extends Named>(
  open: readonly T[],
  all: readonly T[],
  selected: string,
): CodeEntityRow[] {
  const rows = open.map((item) => row(item));
  const chosen = selected !== "" && !open.some((item) => item.id === selected);
  const kept = chosen ? all.find((item) => item.id === selected) : undefined;
  return kept !== undefined ? [...rows, row(kept, true)] : rows;
}

/** Refusals about a line's branch or cost centre: they mark that cell, by the dimension they name. */
const DIMENSION_CODES = new Set(["ACCT_DIMENSION_INACTIVE", "ACCT_DIMENSION_REQUIRED"]);
const cellOf = (problem: Problem): "account" | "BRANCH" | "COST_CENTER" => {
  const dimension = problem.args.dimension;
  return DIMENSION_CODES.has(problem.code) &&
    (dimension === "BRANCH" || dimension === "COST_CENTER")
    ? dimension
    : "account";
};

/** The totals row: the label spans the columns before Debit. */
function Totals({
  debit,
  credit,
  currency,
  removable,
}: {
  debit: string;
  credit: string;
  currency: string;
  removable: boolean;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  return (
    <tfoot>
      <DataTableRow className="font-semibold">
        <DataTableCell colSpan={5} className="text-end">
          {t("accounting:journal.total")}
        </DataTableCell>
        <DataTableCell className="text-end tabular-nums">{f.money(debit, currency)}</DataTableCell>
        <DataTableCell className="text-end tabular-nums">{f.money(credit, currency)}</DataTableCell>
        {removable ? <DataTableCell /> : null}
      </DataTableRow>
    </tfoot>
  );
}

export function JournalGrid({
  lines,
  onLineChange,
  onRemove,
  onLastEnter,
  refs,
  currency,
  totals,
  problems,
  disabled = false,
}: {
  lines: readonly GridLine[];
  onLineChange: (line: GridLine) => void;
  onRemove: (lineNo: number) => void;
  /** Enter in the last line's last cell. */
  onLastEnter: () => void;
  refs: GridRefs;
  currency: string;
  /** The typed lines' totals; null while an amount isn't one. */
  totals: GridTotals | null;
  /** As last saved or refused; only those with a line number show here. */
  problems: readonly Problem[];
  disabled?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation("accounting");
  const f = useLedgerFormat();
  const accountsById = new Map(refs.accounts.map((account) => [account.id, account]));
  const activeBranches = refs.branches.filter((branch) => branch.active !== false);
  const activeCostCentres = refs.costCentres.filter((centre) => centre.active !== false);
  return (
    <div className="overflow-x-auto rounded-md border border-subtle">
      <DataTable density="compact">
        <GridHead removable />
        <DataTableBody>
          {lines.map((line, index) => {
            const set = (changes: Partial<GridLine>): void => onLineChange({ ...line, ...changes });
            const missing = missingCells(accountsById.get(line.accountId), line);
            const own = problems.filter((problem) => problem.lineNo === line.lineNo);
            const messages = own.map((problem) => problemText(f, t, i18n, problem, currency));
            const cells = new Set(own.map(cellOf));
            const last = index === lines.length - 1;
            return (
              <DataTableRow key={line.lineNo} data-jline={line.lineNo} className="align-top">
                <DataTableCell className="min-w-56">
                  <CodeEntityCombobox
                    ariaLabel={t("accounting:journal.columns.account")}
                    placeholder={t("accounting:journal.choose_account")}
                    value={line.accountId}
                    onChange={(accountId) => set({ accountId })}
                    clearable={false}
                    disabled={disabled}
                    invalid={cells.has("account")}
                    items={choices(refs.pickable, refs.accounts, line.accountId)}
                  />
                  {messages.map((message) => (
                    <span key={message} className="block text-label text-danger">
                      {message}
                    </span>
                  ))}
                </DataTableCell>
                <DataTableCell className="min-w-40">
                  <Input
                    aria-label={t("accounting:journal.columns.description")}
                    value={line.description}
                    disabled={disabled}
                    onChange={(event) => set({ description: event.target.value })}
                  />
                </DataTableCell>
                {/* Pickers keep their value on one line; the table scrolls before they wrap. */}
                <DataTableCell className="min-w-44 whitespace-nowrap">
                  <CodeEntityCombobox
                    ariaLabel={t("accounting:journal.columns.contact")}
                    placeholder={t("accounting:journal.no_contact")}
                    value={line.contactId}
                    onChange={(contactId) => set({ contactId })}
                    disabled={disabled}
                    invalid={missing.includes("contact")}
                    items={choices(refs.contacts, refs.contacts, line.contactId)}
                  />
                </DataTableCell>
                <DataTableCell className="min-w-40 whitespace-nowrap">
                  <CodeEntityCombobox
                    ariaLabel={t("accounting:journal.columns.branch")}
                    placeholder={t("accounting:ledger.journal.no_branch")}
                    value={line.branchId}
                    onChange={(branchId) => set({ branchId })}
                    disabled={disabled}
                    invalid={missing.includes("branch") || cells.has("BRANCH")}
                    items={choices(activeBranches, refs.branches, line.branchId)}
                  />
                </DataTableCell>
                <DataTableCell className="min-w-40 whitespace-nowrap">
                  <CodeEntityCombobox
                    ariaLabel={t("accounting:journal.columns.cost_centre")}
                    placeholder={t("accounting:ledger.journal.no_cost_centre")}
                    value={line.costCentreId}
                    onChange={(costCentreId) => set({ costCentreId })}
                    disabled={disabled}
                    invalid={missing.includes("costCentre") || cells.has("COST_CENTER")}
                    items={choices(activeCostCentres, refs.costCentres, line.costCentreId)}
                  />
                </DataTableCell>
                {(["debit", "credit"] as const).map((side) => (
                  <DataTableCell key={side} className="min-w-32">
                    <MoneyInput
                      aria-label={
                        side === "debit"
                          ? t("accounting:journal.columns.debit")
                          : t("accounting:journal.columns.credit")
                      }
                      value={line[side]}
                      currency={currency}
                      disabled={disabled}
                      // Not an amount yet (or emptied): the side is left out of the request.
                      onChange={(amount) =>
                        set(side === "debit" ? { debit: amount ?? "" } : { credit: amount ?? "" })
                      }
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter" &&
                          side === "credit" &&
                          last &&
                          !event.ctrlKey &&
                          !event.metaKey
                        ) {
                          event.preventDefault();
                          onLastEnter();
                        }
                      }}
                    />
                  </DataTableCell>
                ))}
                <DataTableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    aria-label={nonEmpty(t("accounting:journal.remove_line"))}
                    onClick={() => onRemove(line.lineNo)}
                  >
                    <Icon as={Trash2} size={16} />
                  </Button>
                </DataTableCell>
              </DataTableRow>
            );
          })}
        </DataTableBody>
        {totals !== null ? (
          <Totals debit={totals.debit} credit={totals.credit} currency={currency} removable />
        ) : null}
      </DataTable>
    </div>
  );
}

/** A line as read: names already resolved, amounts as the server sent them. */
export interface ViewLine {
  lineNo: number;
  account: string;
  description: string;
  /** Under the description, e.g. the foreign amount and its rate. */
  note?: string;
  contact: string;
  branch: string;
  costCentre: string;
  debit: string;
  credit: string;
}

/** Lines as posted or saved, read-only; a zero side stays empty. */
export function JournalGridView({
  lines,
  totals,
  currency,
}: {
  lines: readonly ViewLine[];
  totals: { debit: string; credit: string } | null;
  currency: string;
}): JSX.Element {
  const f = useLedgerFormat();
  const amount = (value: string): string =>
    value === "" || ZERO.test(value) ? "" : f.money(value, currency);
  return (
    <div className="overflow-x-auto rounded-md border border-subtle">
      <DataTable density="compact">
        <GridHead />
        <DataTableBody>
          {lines.map((line) => (
            <DataTableRow key={line.lineNo}>
              <DataTableCell>
                <bdi>{line.account}</bdi>
              </DataTableCell>
              <DataTableCell>
                {line.description}
                {line.note !== undefined ? (
                  <span className="block text-label text-muted">{line.note}</span>
                ) : null}
              </DataTableCell>
              <DataTableCell>
                <bdi>{line.contact}</bdi>
              </DataTableCell>
              <DataTableCell>{line.branch}</DataTableCell>
              <DataTableCell>{line.costCentre}</DataTableCell>
              <DataTableCell className="text-end tabular-nums">{amount(line.debit)}</DataTableCell>
              <DataTableCell className="text-end tabular-nums">{amount(line.credit)}</DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
        {totals !== null ? (
          <Totals
            debit={totals.debit}
            credit={totals.credit}
            currency={currency}
            removable={false}
          />
        ) : null}
      </DataTable>
    </div>
  );
}
